/**
 * SaveQueue.ts
 * Service centralisé de gestion de sauvegarde avec queue pour éviter les race conditions
 * Garantit qu'une seule sauvegarde est en cours à la fois
 */
import { logger } from '../utils/ProfessionalLogger';

export interface SaveTask<T = void> {
  id: string;
  fn: () => Promise<T>;
  priority: 'high' | 'normal' | 'low';
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timestamp: number;
}

/**
 * Service de queue pour les sauvegardes
 * Évite les race conditions en traitant les sauvegardes séquentiellement
 */
export class SaveQueue {
  private queue: SaveTask<unknown>[] = [];
  private processing = false;
  private currentTask: SaveTask<unknown> | null = null;
  private maxRetries = 3;
  private retryDelay = 1000; // 1 seconde

  /**
   * Ajoute une tâche de sauvegarde à la queue
   * @param fn Fonction de sauvegarde à exécuter
   * @param priority Priorité de la sauvegarde
   * @returns Promise qui se résout avec la valeur retournée par la fonction
   */
  async enqueue<T = void>(
    fn: () => Promise<T>,
    priority: 'high' | 'normal' | 'low' = 'normal'
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const task: SaveTask<T> = {
        id: `save-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        fn,
        priority,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      // Insérer selon la priorité
      if (priority === 'high') {
        // Les tâches haute priorité vont au début
        this.queue.unshift(task as SaveTask<unknown>);
      } else if (priority === 'low') {
        // Les tâches basse priorité vont à la fin
        this.queue.push(task as SaveTask<unknown>);
      } else {
        // Les tâches normales vont après les hautes priorités mais avant les basses
        const highPriorityIndex = this.queue.findIndex(t => t.priority !== 'high');
        if (highPriorityIndex === -1) {
          this.queue.push(task as SaveTask<unknown>);
        } else {
          this.queue.splice(highPriorityIndex, 0, task as SaveTask<unknown>);
        }
      }

      logger.debug(`Tâche de sauvegarde ajoutée à la queue (${priority}): ${task.id}`, {
        queueLength: this.queue.length,
      });

      // Démarrer le traitement si pas déjà en cours
      this.process();
    });
  }

  /**
   * Traite la queue de sauvegardes
   */
  private async process(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const task = this.queue.shift();
      if (!task) break;

      this.currentTask = task;

      try {
        logger.debug(`Traitement de la tâche de sauvegarde: ${task.id}`);

        // Exécuter la sauvegarde avec retry et récupérer le résultat
        const result = await this.executeWithRetry(task.fn, this.maxRetries);

        logger.debug(`Tâche de sauvegarde terminée avec succès: ${task.id}`);
        task.resolve(result);
      } catch (error) {
        logger.error(`Échec de la tâche de sauvegarde: ${task.id}`, error);
        task.reject(error instanceof Error ? error : new Error(String(error)));
      } finally {
        this.currentTask = null;
      }
    }

    this.processing = false;
    logger.debug('Queue de sauvegarde vide');
  }

  /**
   * Exécute une fonction avec retry et backoff exponentiel
   */
  private async executeWithRetry<T>(
    fn: () => Promise<T>,
    retries: number,
    delay: number = this.retryDelay
  ): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      if (retries > 0) {
        logger.warn(
          `Tentative de sauvegarde échouée, retry dans ${delay}ms (${retries} tentatives restantes)`,
          error
        );
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.executeWithRetry(fn, retries - 1, delay * 2);
      }
      throw error;
    }
  }

  /**
   * Vide la queue (utile en cas d'urgence)
   */
  clear(): void {
    const clearedTasks = this.queue.length;
    this.queue.forEach(task => {
      task.reject(new Error('Queue vidée avant exécution'));
    });
    this.queue = [];
    logger.warn(`Queue de sauvegarde vidée: ${clearedTasks} tâches annulées`);
  }

  /**
   * Obtient l'état de la queue
   */
  getStatus(): {
    queueLength: number;
    processing: boolean;
    currentTask: string | null;
  } {
    return {
      queueLength: this.queue.length,
      processing: this.processing,
      currentTask: this.currentTask?.id || null,
    };
  }

  /**
   * Vérifie si une sauvegarde est en cours
   */
  isProcessing(): boolean {
    return this.processing;
  }
}

// Export singleton
export const saveQueue = new SaveQueue();
